import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveSeoBaseUrl, SEO_SITE_ALIASES } from '../../utils/seoUtils';

export interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  noIndex?: boolean;
  alternateLanguages?: boolean;
  /** JSON-LD structured data object or array of objects to inject into <head> */
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
}

export const SEO: React.FC<SEOProps> = ({
  title = 'Myanmar Mobile Top Up & Data Packages | EZTopup',
  description = 'Top up MPT, Atom, U9, MyTel and buy data packages or gift cards in Myanmar with instant, secure delivery.',
  keywords = 'mobile topup, data packages, e-gift cards, Myanmar, digital recharge, phone credit, instant topup, MPT, U9, Atom, MyTel, auto topup, coupon topup, merchant topup, affiliate program, ဖုန်းငွေဖြည့်, ဒေတာပက်ကေ့ချ်, အော်တိုတော့ပ်အပ်, ကူပွန်တော့ပ်အပ်',
  image = '/logo.svg',
  url = resolveSeoBaseUrl(),
  type = 'website',
  noIndex = false,
  alternateLanguages = true,
  structuredData,
}) => {
  const { i18n } = useTranslation();
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;

      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }

      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('robots', noIndex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:type', type, true);

    // Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    // Helper function to generate language-specific URL
    const generateLanguageUrl = (baseUrl: string, language: string) => {
      const urlObj = new URL(baseUrl);

      // Ensure trailing slash for consistency
      if (!urlObj.pathname.endsWith('/') && urlObj.pathname !== '/') {
        urlObj.pathname += '/';
      }

      if (language === 'mm') {
        urlObj.searchParams.set('lang', 'my'); // Use 'my' in URL for Myanmar
      } else if (language === 'en') {
        urlObj.searchParams.delete('lang'); // Default language, no param needed
      }
      return urlObj.toString();
    };

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    // Set canonical URL based on current language
    const canonicalUrl = generateLanguageUrl(url, i18n.language);
    canonical.setAttribute('href', canonicalUrl);

    // Add alternate language links (hreflang)
    if (alternateLanguages) {
      // Remove existing hreflang links
      const existingHreflangs = document.querySelectorAll('link[hreflang]');
      existingHreflangs.forEach((link) => link.remove());
      const existingDomainAliases = document.querySelectorAll('link[data-seo-domain-alias="true"]');
      existingDomainAliases.forEach((link) => link.remove());

      // Add hreflang for English (default)
      const hreflangEn = document.createElement('link');
      hreflangEn.setAttribute('rel', 'alternate');
      hreflangEn.setAttribute('hreflang', 'en');
      hreflangEn.setAttribute('href', generateLanguageUrl(url, 'en'));
      document.head.appendChild(hreflangEn);

      // Add hreflang for Myanmar
      const hreflangMy = document.createElement('link');
      hreflangMy.setAttribute('rel', 'alternate');
      hreflangMy.setAttribute('hreflang', 'my');
      hreflangMy.setAttribute('href', generateLanguageUrl(url, 'mm'));
      document.head.appendChild(hreflangMy);

      // Add x-default hreflang
      const hreflangDefault = document.createElement('link');
      hreflangDefault.setAttribute('rel', 'alternate');
      hreflangDefault.setAttribute('hreflang', 'x-default');
      hreflangDefault.setAttribute('href', generateLanguageUrl(url, 'en'));
      document.head.appendChild(hreflangDefault);

      // Add alternate domain links for domain alias discovery
      const canonicalOrigin = new URL(canonicalUrl).origin;
      SEO_SITE_ALIASES.forEach((domain: string) => {
        const domainOrigin = domain.replace(/\/$/, '');
        if (domainOrigin === canonicalOrigin) {
          return;
        }

        const canonicalObj = new URL(canonicalUrl);
        const aliasObj = new URL(domainOrigin);
        aliasObj.pathname = canonicalObj.pathname;
        aliasObj.search = canonicalObj.search;

        const aliasLink = document.createElement('link');
        aliasLink.setAttribute('rel', 'alternate');
        aliasLink.setAttribute('href', aliasObj.toString());
        aliasLink.setAttribute('data-seo-domain-alias', 'true');
        document.head.appendChild(aliasLink);
      });
    }
  }, [title, description, keywords, image, url, type, noIndex, alternateLanguages, i18n.language]);

  // Inject per-page structured data into <head>, cleaned up on unmount or change
  useEffect(() => {
    if (!structuredData) return;

    const schemas = Array.isArray(structuredData) ? structuredData : [structuredData];
    const elements: HTMLScriptElement[] = schemas.map((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-dynamic', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      return script;
    });

    return () => {
      elements.forEach((el) => el.remove());
    };
  }, [structuredData]);

  return null;
};

export default SEO;
